export type ChannelInput = { label: string; value: string };
export const channelTypes = ["WhatsApp", "Instagram", "Telefone", "Site", "E-mail", "Outro"];
export function structuredProposalChannels(raw: string): ChannelInput[] | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length <= 12 && parsed.every(item => item && typeof item === "object" && Object.keys(item).length === 2 && typeof item.label === "string" && typeof item.value === "string")) return parsed.map(item => ({ label: item.label, value: item.value }));
  } catch { /* A prose proposal must be reviewed by the owner. */ }
  return null;
}
export function proposalChannels(raw: string): ChannelInput[] {
  const structured = structuredProposalChannels(raw);
  if (structured) return structured;
  // Only an unambiguous, explicitly labelled standalone phone sentence is
  // recoverable. Other prose, including mentions or exclusions, remains in
  // the original proposal and never becomes a channel automatically.
  if (raw.split(/[.!?\n]/u).some(sentence => /whatsapp/iu.test(sentence) && /(?:\bn[aã]o\b|\bnunca\b|\bsem\b)/iu.test(sentence))) return [];
  const matches = [...raw.matchAll(/(?:^|[.!?\n]\s*)WhatsApp\s*:\s*(\+?\d[\d ()-]*|\(\d{2}\)[\d ()-]*)\s*(?=[.!?\n]|$)/giu)];
  if (matches.length !== 1) return [];
  const value = matches[0]![1]!.trim();
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 13 ? [{ label: "WhatsApp", value }] : [];
}
export function channelHint(label: string) {
  switch (label) {
    case "WhatsApp": return { label: "Número do WhatsApp", placeholder: "Ex.: (12) 99999-9999", type: "tel" };
    case "Telefone": return { label: "Número de telefone", placeholder: "Ex.: (12) 3333-3333", type: "tel" };
    case "Instagram": return { label: "Usuário ou link do Instagram", placeholder: "Ex.: @suaempresa", type: "text" };
    case "Site": return { label: "Endereço do site", placeholder: "Ex.: https://suaempresa.com.br", type: "url" };
    case "E-mail": return { label: "Endereço de e-mail", placeholder: "Ex.: contato@suaempresa.com.br", type: "email" };
    default: return { label: "Contato ou link deste canal", placeholder: "Informe o identificador real", type: "text" };
  }
}
export function channelInputError(channels: readonly ChannelInput[]): string {
  if (channels.length > 12) return "Você pode registrar até 12 canais.";
  if (channels.some(item => !item.label.trim() || !item.value.trim())) return "Informe o contato de cada canal que deseja salvar. Se ainda não souber, deixe para depois sem aplicar a proposta.";
  if (channels.some(item => item.label.trim().length > 80 || item.value.trim().length > 240)) return "Use até 80 caracteres para o canal e 240 para o contato.";
  const keys = channels.map(item => `${item.label.trim().toLocaleLowerCase("pt-BR")}\u0000${item.value.trim().toLocaleLowerCase("pt-BR")}`);
  if (new Set(keys).size !== keys.length) return "Remova canais duplicados antes de salvar.";
  return "";
}
export function canApplyChannelProposal(raw: string): boolean {
  const channels = structuredProposalChannels(raw);
  return Boolean(channels?.length && !channelInputError(channels));
}
export function channelDisplay(raw: string): string {
  try {
    const value: unknown = JSON.parse(raw);
    if (Array.isArray(value) && value.every(item => typeof item?.label === "string" && typeof item?.value === "string")) return value.map(item => `${item.label}: ${item.value}`).join(" · ");
  } catch { /* Keep the original owner-visible prose. */ }
  return raw;
}
